import React, { createContext, useContext, useState } from "react";
import gsap from "gsap";
import { normalizeAnimationData } from "../../utils/animationDataUtils";
import { buildAnimationProps } from "./animations/animationBuilder";

const AnimationContext = createContext(null);

export function useAnimation() {
  return useContext(AnimationContext);
}

// Generates text elements for a question and its choices so the editor pre-populates
// them as draggable/animatable canvas elements the first time the editor opens.
// Positions/sizes mirror the non-animated gray form: a question-type heading, the
// question text, then the choices — left-aligned and vertically grouped. Every
// element is an editable text element the creator can move, resize and re-word.
function buildQuestionElements(slideContent) {
  const mkId = () => "el_" + Math.random().toString(36).substring(2, 9);
  const els = [];

  const typeLabel =
    slideContent.questionType === "multiple"
      ? "Multiple Choice Question"
      : slideContent.questionType === "text"
        ? "Text Question"
        : "Poll Question";

  // Question-type heading (editable) — matches the form's "Poll Question" h4
  els.push({
    id: mkId(),
    type: "text",
    name: "Heading",
    // text: typeLabel,
    fontSize: 26,
    color: "#111827",
    elementRole: "heading",
    state: {
      position: { x: 5, y: 26 },
      size: { w: 70, h: 9 },
      rotation: 0,
      opacity: 1,
      scale: 1,
    },
  });

  // Question text
  els.push({
    id: mkId(),
    type: "text",
    name: "Question",
    text: slideContent.question || "Question",
    fontSize: 26,
    color: "#374151",
    elementRole: "question",
    state: {
      position: { x: 5, y: 36 },
      size: { w: 85, h: 9 },
      rotation: 0,
      opacity: 1,
      scale: 1,
    },
  });

  // One element per choice, stacked below the question (matches space-y-3).
  // elementRole + choiceIndex let the views render interactive click targets
  // exactly at each choice's canvas position.
  (slideContent.choices || []).forEach((choice, i) => {
    els.push({
      id: mkId(),
      type: "text",
      name: `Choice ${i + 1}`,
      text: choice,
      fontSize: 20,
      color: "#374151",
      borderColor: "#d1d5db",
      backgroundColor: "rgba(255,255,255,0.5)",
      elementRole: "choice",
      choiceIndex: i,
      state: {
        position: { x: 5, y: 48 + i * 9 },
        size: { w: 80, h: 7 },
        rotation: 0,
        opacity: 1,
        scale: 1,
      },
    });
  });

  return els;
}

export function AnimationProvider({
  children,
  initialBackground = null,
  initialBackgroundColor = null,
  initialData = null,
  initialAssets = [],
  onUploadAsset = null,
  slideContent = null,
}) {
  const normalizedInitialData = normalizeAnimationData(initialData);

  const [assets, setAssets] = useState(initialAssets);
  const [assetFiles, setAssetFiles] = useState({}); // tempKey -> File, pending upload at save time
  const [elements, setElements] = useState(() => {
    // Prefer existing saved elements over auto-generation
    if (normalizedInitialData?.elements?.length > 0) {
      return normalizedInitialData.elements;
    }
    // First-time open for a question slide: seed the canvas with question + choices
    if (slideContent) {
      return buildQuestionElements(slideContent);
    }
    return [];
  });
  const [interactions, setInteractions] = useState(
    normalizedInitialData?.interactions || [],
  );
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(
    initialData?.totalDuration || 3.0,
  );
  const [gsapTimeline, setGsapTimeline] = useState(null);
  const [activeRightTab, setActiveRightTab] = useState("Animate");
  // Once animation data has been saved at least once, its canvasBackground is the
  // source of truth — even when it's explicitly null (user cleared it). Only fall
  // back to the slide's own image (initialBackground) the very first time the
  // editor opens for this slide, before any animation data exists at all.
  // (`?? initialBackground` here would be wrong: `??` treats an explicit `null`
  // the same as "unset" and would resurrect a background the user deliberately removed.)
  const [canvasBackground, setCanvasBackground] = useState(
    normalizedInitialData
      ? (normalizedInitialData.canvasBackground ?? null)
      : initialBackground,
  );
  const [canvasBackgroundKey, setCanvasBackgroundKey] = useState(
    normalizedInitialData?.canvasBackgroundKey ?? null,
  );
  const [canvasBackgroundColor, setCanvasBackgroundColor] = useState(
    normalizedInitialData?.canvasBackgroundColor ??
      initialBackgroundColor ??
      // Question slides use a light/gray canvas (dark text); other slides dark.
      (slideContent ? "#f3f4f6" : "#1a1a2e"),
  );

  const selectedElement =
    elements.find((el) => el.id === selectedElementId) || null;

  const getTimelineActions = () => {
    const match = interactions.find(
      (int) => int && int.event === "slide_start",
    );
    return match ? match.actions : [];
  };

  // Update top-level element fields (text, name, src, etc.)
  const updateElement = (elementId, updates) => {
    setElements((prev) =>
      prev.map((el) => (el.id === elementId ? { ...el, ...updates } : el)),
    );
  };

  // Update element.state fields (position, size, opacity, rotation, scale)
  const updateElementState = (elementId, updates) => {
    setElements((prev) =>
      prev.map((el) =>
        el.id === elementId
          ? { ...el, state: { ...el.state, ...updates } }
          : el,
      ),
    );
  };

  const addTextElement = () => {
    const newEl = {
      id: "el_" + Math.random().toString(36).substring(2, 7),
      type: "text",
      name: "Text",
      text: "New Text",
      color: "#ffffff",
      fontSize: 28,
      state: {
        position: { x: 10, y: 10 },
        size: { w: 25, h: 8 },
        rotation: 0,
        opacity: 1,
        scale: 1,
      },
    };
    setElements((prev) => [...prev, newEl]);
    setSelectedElementId(newEl.id);
  };

  const addNewElementFromAsset = (asset, x, y) => {
    const newEl = {
      id: "el_" + Math.random().toString(36).substring(2, 7),
      type: "image",
      name: asset.name,
      src: asset.thumbnail || asset.src || null,
      assetKey: asset.tempKey || null, // pending upload key, resolved to a real URL at save time
      state: {
        position: { x: x ?? 35, y: y ?? 35 },
        size: { w: 30, h: 30 },
        rotation: 0,
        opacity: 1,
        scale: 1,
      },
    };
    setElements((prev) => [...prev, newEl]);
    setSelectedElementId(newEl.id);
  };

  const deleteElement = (elementId) => {
    setElements((prev) => prev.filter((el) => el.id !== elementId));
    setInteractions((prev) =>
      prev
        .map((int) => ({
          ...int,
          actions: int.actions.filter((act) => act.element_id !== elementId),
        }))
        .filter((int) => int.actions.length > 0),
    );
    if (selectedElementId === elementId) setSelectedElementId(null);
  };

  // Registers a file for upload at final save time and returns its temp placeholder key
  const registerPendingFile = (file) => {
    const tempKey = "temp_asset_" + Math.random().toString(36).substring(2, 9);
    setAssetFiles((prev) => ({ ...prev, [tempKey]: file }));
    return tempKey;
  };

  const uploadAsset = (file) => {
    const tempKey = registerPendingFile(file);
    setAssets((prev) => [
      {
        id: tempKey,
        name: file.name,
        type: "image",
        thumbnail: URL.createObjectURL(file),
        tempKey,
      },
      ...prev,
    ]);
  };

  const setCanvasBackgroundFile = (file) => {
    const tempKey = registerPendingFile(file);
    setCanvasBackground(URL.createObjectURL(file));
    setCanvasBackgroundKey(tempKey);
  };

  const clearCanvasBackground = () => {
    setCanvasBackground(null);
    setCanvasBackgroundKey(null);
  };

  const addAction = (interactionEvent, interactionTarget, actionData) => {
    const newAction = {
      id: "act_" + Math.random().toString(36).substring(2, 7),
      element_id: actionData.element_id || selectedElementId,
      type: actionData.type || "move",
      to: actionData.to || { x: 10, y: 10 },
      duration: actionData.duration || 1.0,
      delay: actionData.delay || 0.0,
      ease: actionData.ease || "Power2.out",
    };
    setInteractions((prev) => {
      const match = prev.find(
        (int) =>
          int.event === interactionEvent && int.target === interactionTarget,
      );
      if (match) {
        return prev.map((int) =>
          int === match
            ? { ...int, actions: [...int.actions, newAction] }
            : int,
        );
      }
      return [
        ...prev,
        {
          id: "int_" + Math.random().toString(36).substring(2, 7),
          event: interactionEvent,
          target: interactionTarget,
          actions: [newAction],
        },
      ];
    });
  };

  const updateAction = (actionId, updates) => {
    setInteractions((prev) =>
      prev.map((int) => ({
        ...int,
        actions: int.actions.map((act) =>
          act.id === actionId ? { ...act, ...updates } : act,
        ),
      })),
    );
  };

  const deleteAction = (actionId) => {
    setInteractions((prev) =>
      prev
        .map((int) => ({
          ...int,
          actions: int.actions.filter((act) => act.id !== actionId),
        }))
        .filter((int) => int.actions.length > 0),
    );
    if (selectedActionId === actionId) setSelectedActionId(null);
  };

  const resetCanvasElements = (els) => {
    els.forEach((el) => {
      const target = document.getElementById(`canvas-el-${el.id}`);
      if (!target) return;
      gsap.set(target, {
        left: el.state.position.x + "%",
        top: el.state.position.y + "%",
        x: 0,
        y: 0,
        width: el.state.size.w + "%",
        height: el.state.size.h + "%",
        rotation: el.state.rotation || 0,
        opacity: el.state.opacity ?? 1,
        scale: el.state.scale || 1,
      });
    });
  };

  const runGSAPPreview = () => {
    if (gsapTimeline) {
      gsapTimeline.kill();
      resetCanvasElements(elements);
    }

    const tl = gsap.timeline({
      onUpdate: () => setCurrentTime(tl.time()),
      onComplete: () => {
        setIsPlaying(false);
      },
    });

    // Elements with a fade action should start at opacity 0 so the fade plays visibly
    const fadeActionElIds = new Set(
      getTimelineActions()
        .filter((act) => act.type === "fade")
        .map((act) => act.element_id),
    );

    const isMobile = window.innerWidth < 992;

    elements.forEach((el) => {
      const target = document.getElementById(`canvas-el-${el.id}`);
      if (!target) return;
      gsap.set(target, {
        left: el.state.position.x + "%",
        top:
          isMobile && el.elementRole === "question"
            ? "5vw"
            : el.state.position.y + "%",
        // top: el.state.position.y + "%",
        x: 0,
        y: 0,
        width: el.state.size.w + "%",
        height: el.state.size.h + "%",
        rotation: el.state.rotation || 0,
        opacity: fadeActionElIds.has(el.id) ? 0 : (el.state.opacity ?? 1),
        scale: el.state.scale || 1,
      });
    });

    getTimelineActions().forEach((act) => {
      const target = document.getElementById(`canvas-el-${act.element_id}`);
      if (!target) return;
      const animProps = buildAnimationProps(act);
      if (!animProps) return;
      // Use fromTo for fade so it always starts from opacity 0 regardless of current state
      if (act.type === "fade") {
        tl.fromTo(target, { opacity: 0 }, animProps, act.delay);
      } else {
        tl.to(target, animProps, act.delay);
      }
    });
    setGsapTimeline(tl);
    tl.play();
    setIsPlaying(true);
  };

  const pauseGSAPPreview = () => {
    if (gsapTimeline) {
      gsapTimeline.pause();
      gsapTimeline.kill();
      resetCanvasElements(elements);
      setIsPlaying(false);
    }
  };

  const seekGSAPPreview = (time) => {
    if (gsapTimeline) {
      gsapTimeline.time(time);
      setCurrentTime(time);
    } else {
      setCurrentTime(time);
    }
  };

  // Session-local working copy: keeps src renderable (blob/real URL) and keeps
  // assetKey/canvasBackgroundKey intact so re-opening the editor still shows images
  // and still knows which pending files to upload at final submission time.
  // Placeholder substitution for the backend payload happens separately, only once,
  // right before the final presentation save (see resolveAnimationDataForUpload).
  const getSerializableAnimationData = () => ({
    elements,
    interactions,
    totalDuration,
    canvasBackground,
    canvasBackgroundKey,
    canvasBackgroundColor,
  });

  return (
    <AnimationContext.Provider
      value={{
        assets,
        assetFiles,
        elements,
        setElements,
        interactions,
        setInteractions,
        selectedElementId,
        setSelectedElementId,
        selectedElement,
        selectedActionId,
        setSelectedActionId,
        isPlaying,
        currentTime,
        totalDuration,
        setTotalDuration,
        getTimelineActions,
        updateElement,
        updateElementState,
        addTextElement,
        addNewElementFromAsset,
        uploadAsset,
        addAction,
        updateAction,
        deleteAction,
        deleteElement,
        runGSAPPreview,
        pauseGSAPPreview,
        seekGSAPPreview,
        activeRightTab,
        setActiveRightTab,
        canvasBackground,
        setCanvasBackgroundFile,
        clearCanvasBackground,
        canvasBackgroundColor,
        setCanvasBackgroundColor,
        getSerializableAnimationData,
      }}
    >
      {children}
    </AnimationContext.Provider>
  );
}
